import React, { useEffect } from 'react';
import { Cloud, Server, HardDrive, FolderOpen, RefreshCw, FileText, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import AnimatedSection from '../components/AnimatedSection';
import BundleInquiryForm from '../components/BundleInquiryForm';
import { services, deals } from '../mockData';
import { trackButtonClick, trackPageView } from '../utils/analytics';

const Services = ({ onOpenPopup }) => {
  useEffect(() => {
    trackPageView('/services', 'Services');
  }, []);

  const iconMap = {
    Cloud,
    Server,
    HardDrive,
    FolderOpen,
    RefreshCw,
    FileText
  };

  const handleCTAClick = (ctaName) => {
    trackButtonClick(ctaName, 'services_page');
    onOpenPopup();
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A1F44] to-[#0056D2] py-20">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection animation="fadeInUp">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Our Services
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Comprehensive cloud solutions tailored for your business. From cloud hosting to automated backups, we provide everything you need to run Tally efficiently and securely.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Awareness Section - Did You Know? (b1 image) */}
      <section className="py-24 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection animation="slideInLeft">
              <img
                src="https://tally-bharat.s3.ap-south-1.amazonaws.com/media/tb-media-b1.jpeg"
                alt="Did You Know? 60%+ of Tally Users Still Store Data Locally"
                className="w-full h-auto rounded-3xl shadow-2xl"
                style={{ maxWidth: '1200px', maxHeight: '1200px' }}
              />
            </AnimatedSection>

            <AnimatedSection animation="slideInRight">
              <div className="space-y-6">
                <div className="inline-block bg-[#FF7A00]/10 text-[#FF7A00] px-4 py-2 rounded-full text-sm font-semibold">
                  Important Alert
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44]">
                  Did You Know? 60%+ of Tally Users Still Store Data Locally!
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  One crash = data gone. No backup = no safety net. Downtime = lost business.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="text-[#0056D2] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">Hardware failures can wipe out years of data</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="text-[#0056D2] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">Local storage limits team collaboration</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="text-[#0056D2] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">No automatic backups means constant risk</span>
                  </li>
                </ul>
                <Button
                  onClick={() => handleCTAClick('switch_to_cloud')}
                  className="bg-[#FF7A00] hover:bg-[#e66d00] text-white font-semibold px-8 py-3 rounded-full transition-all duration-200 hover:scale-105"
                >
                  Switch to Cloud — Secure Your Tally Data
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Access Anywhere Section (b3 image) */}
      <section className="py-24 bg-gradient-to-b from-[#F5F7FA] to-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection animation="slideInLeft">
              <div className="space-y-6">
                <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44]">
                  You use Tally. So does everyone. But are you still stuck to one system?
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Access Tally from anywhere with Cloud — No cables. No chaos. Just cloud.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="text-[#0056D2] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">Work from home, office, or anywhere</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="text-[#0056D2] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">Multiple users can access simultaneously</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="text-[#0056D2] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">Real-time collaboration with your team</span>
                  </li>
                </ul>
                <Button
                  onClick={() => handleCTAClick('access_anywhere')}
                  className="bg-[#0056D2] hover:bg-[#0A1F44] text-white font-semibold px-8 py-3 rounded-full transition-all duration-200 hover:scale-105"
                >
                  Access Tally Anywhere — Go Cloud Now
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="slideInRight">
              <img
                src="https://tally-bharat.s3.ap-south-1.amazonaws.com/media/tb-media-b3.jpeg"
                alt="Access Tally from anywhere"
                className="w-full h-auto rounded-3xl shadow-2xl"
                style={{ maxWidth: '1200px', maxHeight: '1200px' }}
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Advanced Security Section (b4 image) */}
      <section className="py-24 bg-gradient-to-b from-[#F5F7FA] to-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection animation="slideInLeft">
              <img
                src="https://tally-bharat.s3.ap-south-1.amazonaws.com/media/tb-media-b4.jpeg"
                alt="Advanced Security Features"
                className="w-full h-auto rounded-3xl shadow-2xl"
                style={{ maxWidth: '1200px', maxHeight: '1200px' }}
              />
            </AnimatedSection>

            <AnimatedSection animation="slideInRight">
              <div className="space-y-6">
                <div className="inline-block bg-[#0056D2]/10 text-[#0056D2] px-4 py-2 rounded-full text-sm font-semibold">
                  New Features
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44]">
                  Advanced Security Features Now Available
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Smarter logins. Stronger control. Seamless protection for your Tally data.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="text-[#0056D2] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">Multi-factor authentication for enhanced security</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="text-[#0056D2] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">Role-based access control for team members</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="text-[#0056D2] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">Real-time security monitoring and alerts</span>
                  </li>
                </ul>
                <Button
                  onClick={() => handleCTAClick('access_beta')}
                  className="bg-[#0056D2] hover:bg-[#0A1F44] text-white font-semibold px-8 py-3 rounded-full transition-all duration-200 hover:scale-105"
                >
                  Access the Beta Inside Your Cloud Portal
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Products & Services Section - Matching Homepage */}
      <section className="py-24 bg-gradient-to-b from-white to-[#F5F7FA]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44] mb-4">
                Our Products & Services
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Comprehensive cloud solutions tailored for your business
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Tally on Cloud',
              'Custom ERP on Cloud (Marg, Busy, Miracle, and more)',
              'Bare Metal Servers',
              'Dedicated Virtual Machines (Managed & Unmanaged)',
              'Backup Solutions (Acronis Partner)',
              'Custom Backup Solutions',
              'Tally Software Customization'
            ].map((service, index) => (
              <AnimatedSection key={index} animation="fadeInUp" delay={index * 50}>
                <div className="group bg-gradient-to-br from-white via-[#F5F7FA] to-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-transparent hover:border-[#0056D2] relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0056D2]/5 to-[#FF7A00]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl" />
                  
                  <div className="relative flex items-start space-x-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0056D2] to-[#0A1F44] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-md">
                      <CheckCircle2 size={20} className="text-white" />
                    </div>
                    <div>
                      <span className="text-[#0A1F44] font-bold text-lg group-hover:text-[#0056D2] transition-colors duration-300 block mb-1">{service}</span>
                      <div className="h-1 w-0 bg-gradient-to-r from-[#0056D2] to-[#FF7A00] group-hover:w-full transition-all duration-500 rounded-full" />
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Bundle Deals Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44] mb-4">
                Bundle Deals
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Save more with our specially curated service bundles
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {deals.map((deal, index) => (
              <AnimatedSection key={index} animation="fadeInUp" delay={index * 150}>
                <div className="bg-gradient-to-br from-[#F5F7FA] to-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 relative overflow-hidden group h-full flex flex-col">
                  {deal.badge && (
                    <div className="absolute top-0 right-0 bg-gradient-to-r from-[#FF7A00] to-[#e66d00] text-white px-6 py-2 rounded-bl-3xl font-semibold text-sm">
                      {deal.badge}
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-[#0A1F44] mb-3 mt-4">{deal.title}</h3>
                  <p className="text-gray-600 mb-6">{deal.description}</p>
                  <div className="text-4xl font-bold text-[#0056D2] mb-6">{deal.price}</div>
                  <ul className="space-y-3 mb-8 flex-grow">
                    {deal.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center space-x-3">
                        <CheckCircle2 size={20} className="text-[#0056D2] flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handleCTAClick(`select_${deal.title.toLowerCase().replace(' ', '_')}`)}
                    className="w-full bg-[#0056D2] hover:bg-[#0A1F44] text-white font-semibold py-3 rounded-full transition-all duration-200 group-hover:scale-105"
                  >
                    Get Started
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </div>
              </AnimatedSection>
            ))}
          </div>

          {/* Bundle Inquiry Form */}
          <div className="mt-16 max-w-4xl mx-auto">
            <AnimatedSection animation="fadeInUp">
              <BundleInquiryForm />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#0056D2] to-[#0A1F44]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection animation="fadeInUp">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Need a Custom Solution?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Let's discuss your specific requirements and create a tailored plan for your business.
            </p>
            <Button
              onClick={() => handleCTAClick('request_demo')}
              className="bg-[#FF7A00] hover:bg-[#e66d00] text-white font-semibold px-10 py-6 text-lg rounded-full transition-all duration-200 hover:scale-105 hover:shadow-2xl"
            >
              Request a Demo
            </Button>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Services;